<template>
  <span v-if="items.length <= 1" class="font-medium">
    {{ items[0]?.title ?? "-" }}
  </span>

  <span v-else>
    <!-- Plain ``<button>`` instead of PrimeVue ``<Button link>`` so the text
         keeps the same font-weight, padding, and line-height as the
         single-item cell — otherwise the row's vertical alignment drifts
         when one row has the button and adjacent rows have plain text. -->
    <button
      type="button"
      class="font-medium text-primary cursor-pointer hover:underline bg-transparent border-0 p-0 leading-inherit align-baseline"
      @click.stop="togglePopover"
    >
      {{ items.length }} {{ noun }}
    </button>
    <Popover ref="popoverRef">
      <ul class="m-0 pl-4 list-disc space-y-1 text-sm text-gray-700">
        <li v-for="item in items" :key="item.id">
          {{ item.title }}
        </li>
      </ul>
    </Popover>
  </span>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import Popover from "primevue/popover";

/**
 * Table cell for a list of items: the title when there is one, otherwise an
 * "N <noun>" link that opens a popover listing them.
 */

// Module-level registry of every ``ItemsPopoverCell`` popover mounted in the
// app. The trigger button uses ``@click.stop`` so PrimeVue's own click-outside
// listener can't see a click on a sibling row's trigger; without this
// coordination, MAI-4560 reproduces — opening a second cell's popover doesn't
// close the first and they overlap.
//
// We unconditionally call ``hide()`` on every other registered popover before
// toggling the current one. ``hide()`` is a safe no-op when the popover is
// already closed, so the registry works even if PrimeVue's own click-outside
// handler closed something behind our back (e.g. user clicked the page body).
//
// Pinned to globalThis so the registry survives any HMR module re-evaluation
// and so every cell mounted in the app coordinates through the same Set.
const REGISTRY_KEY = "__itemsPopoverCellRegistry";
const registry: Set<InstanceType<typeof Popover>> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ((globalThis as any)[REGISTRY_KEY] ??= new Set());

defineProps<{
  items: readonly { id: string | number; title: string }[];
  noun: string;
}>();

const popoverRef = ref<InstanceType<typeof Popover> | null>(null);

function togglePopover(event: Event) {
  const current = popoverRef.value;
  if (!current) return;

  registry.forEach((other) => {
    if (other !== current) other.hide();
  });
  current.toggle(event);
}

onMounted(() => {
  if (popoverRef.value) registry.add(popoverRef.value);
});

onBeforeUnmount(() => {
  if (popoverRef.value) registry.delete(popoverRef.value);
});
</script>
